import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { 
  ArrowRight,
  TreePine,
  Users,
  Globe,
  Heart,
  Target,
  Calendar,
  MapPin,
  TrendingUp,
  Sparkles,
  ChevronRight,
  ExternalLink,
  Play,
  Share2,
  BookOpen,
  MessageCircle,
  Star,
  CheckCircle,
  Clock,
  User,
  Building2,
  Leaf,
  Mountain,
  Sunrise,
  Shield,
  Award,
  Camera
} from "lucide-react";
import HeroImage from "../../assets/images/journey/515442298_18105262984528706_8966027717628310076_n.jpg";

// Counter Animation Hook
const useCountAnimation = (end, duration = 2, startWhen = true) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!startWhen) return;

    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, startWhen]);

  return count;
};

const AnimatedCounter = ({ end, suffix = "" }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const count = useCountAnimation(end, 2, inView);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="min-h-screen overflow-hidden bg-white">
      {/* Big Container */}
      <div className="flex flex-col lg:flex-row min-h-screen max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20 gap-8 sm:gap-12 lg:gap-16 items-center">
        {/* Left Container - Text */}
        <div className="w-full lg:w-1/2 flex items-center order-2 lg:order-1">
          <div className="w-full">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              {/* Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-50 to-green-50 border border-green-200 rounded-full"
              >
                <TreePine className="w-4 h-4 text-green-600 mr-2" />
                <span className="text-green-700 text-sm font-semibold">Heritage Initiative</span>
              </motion.div>

              {/* Main Title */}
              <div>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight"
                >
                  <span className="text-gray-900">Back</span>
                  <span className="bg-gradient-to-r from-green-600 to-green-600 bg-clip-text text-transparent">2</span>
                  <br />
                  <span className="bg-gradient-to-r from-green-600 to-green-600 bg-clip-text text-transparent">Roots</span>
                </motion.h1>
              </div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-lg"
              >
                A transformative movement helping youth rediscover their cultural roots, traditional values, and ancestral wisdom while building bridges between heritage and modernity.
              </motion.p>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="flex flex-wrap gap-6 sm:gap-8 pt-4"
              >
                <div>
                  <div className="text-2xl font-bold text-green-600">Cultural</div>
                  <div className="text-sm text-gray-600 font-medium">Heritage</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">Youth</div>
                  <div className="text-sm text-gray-600 font-medium">Engagement</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">Tradition</div>
                  <div className="text-sm text-gray-600 font-medium">Revival</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Right Container - Rounded Image */}
        <div className="w-full lg:w-1/2 flex items-center justify-center order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative w-full"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-green-600 to-green-600 rounded-3xl opacity-20 blur-2xl"></div>
            <div className="relative w-full h-[400px] sm:h-[500px] lg:h-[600px] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              <img
                src={HeroImage}
                alt="Back2Roots - Global leadership and heritage"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Mission Section
const MissionSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section ref={ref} className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
            Our <span className="text-green-600">Mission</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Bridging the gap between ancient wisdom and modern living through cultural education and heritage preservation
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Left - Mission Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Cultural Renaissance</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Inspiring a new generation to embrace their cultural heritage while adapting to contemporary challenges and opportunities.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Knowledge Preservation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Documenting and sharing traditional practices, stories, and wisdom before they're lost to time.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Community Connection</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Strengthening bonds between generations and fostering dialogue between elders and youth.
                  </p>
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="bg-green-50 rounded-2xl p-6 border border-green-200"
            >
              <h4 className="font-bold text-green-800 mb-3 flex items-center">
                <Sparkles className="w-5 h-5 mr-2" />
                Vision Statement
              </h4>
              <p className="text-green-700 italic">
                "To create a world where youth are deeply rooted in their heritage while confidently navigating the modern world, fostering a generation that honors the past while building the future."
              </p>
            </motion.div>
          </motion.div>

          {/* Right - Mission Image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={HeroImage}
                alt="Cultural heritage and tradition"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              

            </div>

            {/* Floating Element */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -top-6 -right-6 w-24 h-24 bg-green-500 rounded-full flex items-center justify-center shadow-xl"
            >
              <TreePine className="w-8 h-8 text-white" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Impact Section
const ImpactSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const impactStats = [
    { icon: Users, label: "Youth Engagement", text: "Active" },
    { icon: Globe, label: "Global Reach", text: "Growing" },
    { icon: Calendar, label: "Cultural Events", text: "Regular" },
    { icon: BookOpen, label: "Heritage Documentation", text: "Ongoing" },
    { icon: Heart, label: "Community Connection", text: "Strong" },
    { icon: Star, label: "Impact Stories", text: "Inspiring" }
  ];

  return (
    <section ref={ref} className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-green-50 to-green-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
            Initiative <span className="text-green-600">Impact</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Measuring our success through meaningful connections and cultural preservation achievements
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {impactStats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-green-200/50"
            >
              <div className="flex items-center justify-between mb-6">
                <div className={`w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg`}>
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="w-3 h-3 bg-green-400 rounded-full"
                />
              </div>
              
              <div className="text-3xl font-bold text-green-600 mb-2">
                {stat.text}
              </div>
              <p className="text-gray-700 font-semibold">{stat.label}</p>
            </motion.div>
          ))}
        </div>


      </div>
    </section>
  );
};

// Main Back2Roots Component
const Back2RootsInitiative = () => {
  useEffect(() => {
    document.title = "Back2Roots Initiative - Harsh Sanghani";
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <HeroSection />
      <MissionSection />
      <ImpactSection />
    </div>
  );
};

export default Back2RootsInitiative;